<template>
  <li
    :class="clickableClass"
    data-cy="profile-dropdown-options"
  >
    <div
      v-if="icon"
      class="icon-container"
    >
      <ngc-icon :name="icon" />
    </div>

    <!-- ngc-avatar paints slotted content over the image rather than choosing
         between them the way sn-avatar did, so the initials are withheld when
         there is a picture. sn-avatar's xxs was 32px, which is Gator's
         x-small. -->
    <ngc-avatar
      v-if="showAvatar"
      class="option-avatar"
      size="x-small"
      :src="image || ''"
      :avatar-aria-label="mainText"
    >
      <span v-if="!image">{{ mainTextInitials }}</span>
    </ngc-avatar>
    <div class="option-titles">
      <!-- sn-tooltip wrapped its trigger through an activator slot; ngc-tooltip
           instead pairs with a sibling by id, and has no disabled prop, so the
           truncation check that drove :disabled now decides whether the tooltip
           is rendered at all. -->
      <div :id="tooltipId">
        <p :class="`${mainTextClass} ${clickableClass}`">
          {{ mainText }}
        </p>
      </div>
      <ngc-tooltip
        v-if="!disableTooltip"
        :reference-id="tooltipId"
        placement="bottom"
      >
        {{ mainText }}
      </ngc-tooltip>
      <p
        v-if="subtext !== null"
        class="subtext"
      >
        {{ subtext }}
      </p>
    </div>
  </li>
</template>

<script>

export default {
  name: 'ProfileDropdownOption',
  props: {
    mainText: {
      type: String,
      default: '',
      required: true
    },
    subtext: {
      type: String,
      default: '',
      required: false
    },
    showAvatar: {
      type: Boolean,
      default: true,
      required: false
    },
    icon: {
      type: String,
      default: '',
      required: false
    },
    image: {
      type: String,
      default: '',
      required: false
    },
    bold: {
      type: Boolean,
      default: false,
      required: false
    },
    clickable: {
      type: Boolean,
      default: true,
      required: false
    }
  },
  computed: {
    mainTextClass () {
      return this.bold ? 'main-text bold' : 'main-text regular'
    },
    clickableClass () {
      return this.clickable ? 'clickable' : 'cursor-default'
    },
    mainTextInitials () {
      return this.mainText
        .split(' ')
        .slice(0, 2) // max 2 words may be displayed as initials
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },
    disableTooltip () {
      return this.bold ? this.mainText.length < 19 : this.mainText.length < 21
    },
    // ngc-tooltip finds its trigger by id, so each option needs one of its own.
    tooltipId () {
      return `profile-dropdown-option-${this._uid}`
    }
  }
}
</script>

<style lang="scss" scoped>
li {
  display: inline-flex;
  font-family: var(--font-family-body);
  list-style-type: none;
  text-align: left;
  padding: 10px 16px;
  min-height: 40px;

  &.clickable {
    &:hover {
      background: rgba(15, 95, 94, 0.05);
    }
  }

  p {
    margin: 0;
    padding: 0;
  }

  .option-titles {
    display: flex;
    flex-direction: column;
  }
}

.clickable {
  cursor: pointer;
}

.main-text {
  font-size: 17px;
  line-height: 24px;
  max-width: 190px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.bold {
  font-weight: var(--font-weight-bold);
  cursor: default;
}

.regular {
  max-width: 184px;
}

.cursor-default {
  cursor: default !important;
}

.subtext {
  font-size: 13px;
  line-height: 18px;
}

.icon-container {
  display: flex;
  justify-content: center;
  width: 32px;
  margin-right: 8px;
}

/* Replaces the mr-2 sn-avatar carried. The display is set because the
   ngc-avatar host has none of its own and would otherwise sit on the row's
   text baseline - the same fix .header-actions needs in signingRoomApp.vue. */
.option-avatar {
  display: flex;
  margin-right: var(--spacing-5);
}
</style>
